import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, Minus, RefreshCw, Sparkles, 
  Settings, Target, DollarSign, BarChart3, Zap, Check,
  ArrowRight, Brain, Building2, Plus, Trash2, Pencil, ExternalLink,
  History, Calendar, ChevronUp, ChevronDown, Download, Globe, Search
} from 'lucide-react';
import { API } from '../../lib/api';

export default function AdminDynamicPricing() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  
  // Data
  const [competitors, setCompetitors] = useState([]);
  const [customCompetitors, setCustomCompetitors] = useState([]);
  const [marketStats, setMarketStats] = useState({});
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [settings, setSettings] = useState({
    auto_pricing_enabled: false,
    price_strategy: 'competitiva',
    min_price: 70,
    max_price: 200,
    auto_apply_suggestions: false
  });
  
  // Period selection
  const [checkin, setCheckin] = useState('');
  const [checkout, setCheckout] = useState('');
  
  // Dialogs
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addCompetitorOpen, setAddCompetitorOpen] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState(null);
  const [batchUpdateOpen, setBatchUpdateOpen] = useState(false);
  const [batchUpdating, setBatchUpdating] = useState(false);
  const [batchUpdateForm, setBatchUpdateForm] = useState({
    adjustment_type: 'percentage',
    adjustment_value: ''
  });
  
  // Price History
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [priceHistory, setPriceHistory] = useState({ history: [], stats: {} });
  const [historyDays, setHistoryDays] = useState(90);
  
  // Scraping
  const [scrapingId, setScrapingId] = useState(null);
  const [scrapeDialogOpen, setScrapeDialogOpen] = useState(false);
  const [scrapeForm, setScrapeForm] = useState({
    url: '',
    checkin: '',
    checkout: '',
    name: ''
  });
  
  // AI Search
  const [aiSearching, setAiSearching] = useState(false);
  const [aiSearchResult, setAiSearchResult] = useState(null);
  const [aiSearchDialogOpen, setAiSearchDialogOpen] = useState(false);
  const [scrapeLoading, setScrapeLoading] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  
  // Form competitor
  const [competitorForm, setCompetitorForm] = useState({
    name: '',
    price_per_night: '',
    link: '',
    source: 'booking.com',
    type: 'Casa vacanze',
    rating: '',
    notes: ''
  });

  useEffect(() => {
    // Set default dates (next weekend)
    const today = new Date();
    const daysUntilFriday = (5 - today.getDay() + 7) % 7 || 7;
    const friday = new Date(today);
    friday.setDate(today.getDate() + daysUntilFriday);
    const sunday = new Date(friday);
    sunday.setDate(friday.getDate() + 2);
    
    const defaultCheckin = friday.toISOString().split('T')[0];
    const defaultCheckout = sunday.toISOString().split('T')[0];
    
    setCheckin(defaultCheckin);
    setCheckout(defaultCheckout);
    
    // Load data with default dates
    loadDataWithDates(defaultCheckin, defaultCheckout);
    loadCustomCompetitors();
    loadSettings();
  }, []);

  const loadDataWithDates = async (ci, co) => {
    try {
      const response = await axios.get(`${API}/admin/competitors`, {
        params: { checkin: ci, checkout: co },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompetitors(response.data.competitors || []);
      setMarketStats(response.data.market_stats || {});
    } catch (error) {
      console.error('Error loading competitors:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomCompetitors = async () => {
    try {
      const response = await axios.get(`${API}/admin/custom-competitors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomCompetitors(response.data || []);
    } catch (error) {
      console.error('Error loading custom competitors:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    await loadDataWithDates(checkin, checkout);
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/pricing/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  // CRUD Competitor personalizzati
  const resetCompetitorForm = () => {
    setCompetitorForm({
      name: '',
      price_per_night: '',
      link: '',
      source: 'booking.com',
      type: 'Casa vacanze',
      rating: '',
      notes: ''
    });
    setEditingCompetitor(null);
  };

  const saveCompetitor = async () => {
    if (!competitorForm.name || !competitorForm.price_per_night) {
      toast.error('Nome e prezzo sono obbligatori');
      return;
    }

    try {
      const payload = {
        ...competitorForm,
        price_per_night: parseFloat(competitorForm.price_per_night)
      };

      if (editingCompetitor) {
        await axios.put(`${API}/admin/custom-competitors/${editingCompetitor.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Competitor aggiornato');
      } else {
        await axios.post(`${API}/admin/custom-competitors`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Competitor aggiunto');
      }

      setAddCompetitorOpen(false);
      resetCompetitorForm();
      loadCustomCompetitors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const deleteCompetitor = async (id) => {
    if (!confirm('Eliminare questo competitor?')) return;
    
    try {
      await axios.delete(`${API}/admin/custom-competitors/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Competitor eliminato');
      loadCustomCompetitors();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const editCompetitor = (comp) => {
    setEditingCompetitor(comp);
    setCompetitorForm({
      name: comp.name || '',
      price_per_night: comp.price_per_night?.toString() || '',
      link: comp.link || '',
      source: comp.source || 'booking.com',
      type: comp.type || 'Casa vacanze',
      rating: comp.rating || '',
      notes: comp.notes || ''
    });
    setAddCompetitorOpen(true);
  };

  // Batch Update dei prezzi competitor
  const executeBatchUpdate = async () => {
    if (!batchUpdateForm.adjustment_value) {
      toast.error('Inserisci un valore');
      return;
    }
    
    setBatchUpdating(true);
    try {
      const response = await axios.put(`${API}/admin/custom-competitors/batch-update`, {
        adjustment_type: batchUpdateForm.adjustment_type,
        adjustment_value: parseFloat(batchUpdateForm.adjustment_value)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const { updated_count, total } = response.data;
        toast.success(`Aggiornati ${updated_count}/${total} competitor`);
        setBatchUpdateOpen(false);
        setBatchUpdateForm({ adjustment_type: 'percentage', adjustment_value: '' });
        loadCustomCompetitors();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento batch');
    } finally {
      setBatchUpdating(false);
    }
  };

  // Carica storico prezzi
  const loadPriceHistory = async (days = historyDays) => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/admin/custom-competitors/price-history`, {
        params: { days },
        headers: { Authorization: `Bearer ${token}` }
      });
      setPriceHistory(response.data);
    } catch (error) {
      console.error('Error loading price history:', error);
      toast.error('Errore caricamento storico');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistoryDialog = () => {
    setHistoryOpen(true);
    loadPriceHistory();
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return '-';
    const date = new Date(isoDate);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeTypeLabel = (type) => {
    switch(type) {
      case 'manual': return 'Manuale';
      case 'batch_percentage': return 'Batch %';
      case 'batch_fixed': return 'Batch Fisso';
      default: return type;
    }
  };

  // Ricerca AI competitor
  const searchCompetitorsAI = async () => {
    setAiSearching(true);
    try {
      const response = await axios.post(
        `${API}/admin/pricing/search-competitors`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { checkin, checkout }
        }
      );
      
      if (response.data.success) {
        setAiSearchResult(response.data);
        setAiSearchDialogOpen(true);
        toast.success(`Trovati ${response.data.analysis?.competitors?.length || 0} competitor!`);
        // Ricarica lista competitor
        loadCustomCompetitors();
      } else {
        toast.error(response.data.error || 'Ricerca fallita');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore ricerca AI');
    } finally {
      setAiSearching(false);
    }
  };

  // Applica prezzo suggerito dall'AI
  const applyAISuggestedPrice = async () => {
    if (!aiSearchResult?.analysis?.recommended_price?.suggested) {
      toast.error('Nessun prezzo suggerito da applicare');
      return;
    }
    
    const suggestedPrice = aiSearchResult.analysis.recommended_price.suggested;
    const period = aiSearchResult.period; // "2026-07-10 → 2026-07-12"
    const [dateFrom, dateTo] = period.split(' → ');
    
    try {
      const response = await axios.post(
        `${API}/admin/pricing/apply-suggestion`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { 
            date_from: dateFrom,
            date_to: dateTo,
            price: suggestedPrice,
            sync_beds24: true
          }
        }
      );
      
      if (response.data.success) {
        toast.success(`✅ Prezzo €${suggestedPrice}/notte applicato per ${dateFrom} → ${dateTo}!`);
        if (response.data.synced_beds24) {
          toast.success('📡 Sincronizzato anche su Beds24!');
        }
        setAiSearchDialogOpen(false);
        // Ricarica periodi prezzi
        loadPricePeriods();
      } else {
        toast.error(response.data.message || 'Errore applicazione');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore applicazione prezzo');
    }
  };

  // Scraping prezzo da link
  const scrapeCompetitorPrice = async (competitorId) => {
    setScrapingId(competitorId);
    try {
      const response = await axios.post(
        `${API}/admin/custom-competitors/${competitorId}/refresh-price`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const { old_price, new_price, change } = response.data;
        if (old_price !== new_price) {
          toast.success(`Prezzo aggiornato: €${old_price} → €${new_price} (${change > 0 ? '+' : ''}${change}%)`);
        } else {
          toast.info('Prezzo invariato');
        }
        loadCustomCompetitors();
      } else {
        toast.error(response.data.error || 'Scraping fallito');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore scraping');
    } finally {
      setScrapingId(null);
    }
  };

  // Scraping con URL e date personalizzate
  const executeScrape = async () => {
    if (!scrapeForm.url) {
      toast.error('Inserisci un URL');
      return;
    }
    
    setScrapeLoading(true);
    setScrapeResult(null);
    
    try {
      const response = await axios.post(
        `${API}/admin/custom-competitors/scrape-price`,
        {
          url: scrapeForm.url,
          checkin: scrapeForm.checkin || null,
          checkout: scrapeForm.checkout || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setScrapeResult(response.data);
      
      if (response.data.success) {
        toast.success(`Prezzo trovato: €${response.data.price}`);
      } else {
        toast.error(response.data.error || 'Prezzo non trovato');
      }
    } catch (error) {
      toast.error('Errore durante lo scraping');
      setScrapeResult({ success: false, error: error.message });
    } finally {
      setScrapeLoading(false);
    }
  };

  // Aggiungi competitor da risultato scraping
  const addCompetitorFromScrape = async () => {
    if (!scrapeResult?.price) return;
    
    try {
      await axios.post(`${API}/admin/custom-competitors`, {
        name: scrapeResult.name || scrapeForm.name || 'Nuovo Competitor',
        price_per_night: scrapeResult.price,
        link: scrapeForm.url,
        source: scrapeForm.url.includes('airbnb') ? 'airbnb' : 'booking.com',
        type: 'Casa vacanze',
        rating: scrapeResult.rating || '',
        notes: `Prezzo per ${scrapeForm.checkin || 'date non specificate'}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Competitor aggiunto!');
      setScrapeDialogOpen(false);
      setScrapeForm({ url: '', checkin: '', checkout: '', name: '' });
      setScrapeResult(null);
      loadCustomCompetitors();
    } catch (error) {
      toast.error('Errore aggiunta competitor');
    }
  };

  // Combina competitor mock + personalizzati per le statistiche
  const allCompetitors = [...customCompetitors, ...competitors];
  
  const calculateStats = (comps) => {
    if (!comps.length) return {};
    const prices = comps.map(c => c.price_per_night).filter(p => p > 0);
    if (!prices.length) return {};
    return {
      min_price: Math.min(...prices),
      max_price: Math.max(...prices),
      avg_price: (prices.reduce((a, b) => a + b, 0) / prices.length).toFixed(2),
      competitors_count: comps.length
    };
  };

  const combinedStats = customCompetitors.length > 0 
    ? calculateStats(allCompetitors) 
    : marketStats;


  const saveSettings = async () => {
    try {
      await axios.post(`${API}/admin/pricing/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Impostazioni salvate');
      setSettingsOpen(false);
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const refreshCompetitors = async () => {
    setLoading(true);
    await loadDataWithDates(checkin, checkout);
    toast.success('Dati competitor aggiornati');
  };

  const getAiSuggestion = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(
        `${API}/admin/pricing/ai-suggestions`,
        null,
        {
          params: { checkin, checkout },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setAiSuggestion(response.data.suggestions);
        toast.success('Analisi AI completata');
      } else {
        toast.error(response.data.error || 'Errore analisi AI');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore analisi AI');
    } finally {
      setAnalyzing(false);
    }
  };

  const applySuggestion = async () => {
    if (!aiSuggestion?.suggested_price) {
      toast.error('Nessun suggerimento da applicare');
      return;
    }
    
    setApplying(true);
    try {
      const response = await axios.post(
        `${API}/admin/pricing/apply-suggestion`,
        null,
        {
          params: {
            date_from: checkin,
            date_to: checkout,
            price: aiSuggestion.suggested_price,
            sync_beds24: true
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        setAiSuggestion(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore applicazione');
    } finally {
      setApplying(false);
    }
  };

  const getPriceComparisonIcon = (price) => {
    if (!marketStats.avg_price) return <Minus className="w-4 h-4 text-gray-400" />;
    if (price < marketStats.avg_price * 0.9) return <TrendingDown className="w-4 h-4 text-green-500" />;
    if (price > marketStats.avg_price * 1.1) return <TrendingUp className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-yellow-500" />;
  };

  const getStrategyColor = (strategy) => {
    switch (strategy) {
      case 'aggressiva': return 'bg-red-100 text-red-700';
      case 'competitiva': return 'bg-yellow-100 text-yellow-700';
      case 'premium': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Dynamic Pricing">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-[#C5A059]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dynamic Pricing">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <Brain className="w-6 h-6 text-[#C5A059]" />
              Dynamic Pricing AI
            </h2>
            <p className="text-gray-500 text-sm">Analisi competitor e suggerimenti prezzi intelligenti</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/pricing-calendar">
              <Button variant="outline" className="border-purple-500 text-purple-600 hover:bg-purple-50" data-testid="pricing-calendar-btn">
                <Calendar className="w-4 h-4 mr-2" />
                Calendario Prezzi
              </Button>
            </Link>
            <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="settings-btn">
              <Settings className="w-4 h-4 mr-2" />
              Impostazioni
            </Button>
            <Button 
              onClick={getAiSuggestion}
              disabled={analyzing}
              className="bg-gradient-to-r from-[#C5A059] to-[#D4AF37] hover:from-[#B08A3E] hover:to-[#C5A059]"
              data-testid="analyze-btn"
            >
              {analyzing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analizzando...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Analizza con AI</>
              )}
            </Button>
          </div>
        </div>

        {/* Period Selector */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-xs text-gray-500">Check-in</Label>
                <Input
                  type="date"
                  value={checkin}
                  onChange={(e) => setCheckin(e.target.value)}
                  className="w-40"
                />
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400 mb-2" />
              <div>
                <Label className="text-xs text-gray-500">Check-out</Label>
                <Input
                  type="date"
                  value={checkout}
                  onChange={(e) => setCheckout(e.target.value)}
                  className="w-40"
                />
              </div>
              <Button variant="outline" onClick={refreshCompetitors} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Aggiorna
              </Button>
              
              <Button 
                onClick={searchCompetitorsAI} 
                disabled={aiSearching}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {aiSearching ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Cercando...</>
                ) : (
                  <><Search className="w-4 h-4 mr-2" /> 🔍 Cerca Prezzi AI</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* AI Suggestion Card */}
        {aiSuggestion && (
          <Card className="border-2 border-[#C5A059] bg-gradient-to-br from-amber-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#C5A059]" />
                Suggerimento AI
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Prezzo Suggerito */}
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-sm text-gray-500 mb-1">Prezzo Suggerito</p>
                  <p className="text-4xl font-bold text-[#C5A059]">
                    €{aiSuggestion.suggested_price}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">per notte</p>
                </div>
                
                {/* Range Prezzo */}
                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-sm text-gray-500 mb-2">Range Consigliato</p>
                  <div className="flex justify-between items-center">
                    <span className="text-lg">€{aiSuggestion.price_range?.min || '-'}</span>
                    <div className="flex-1 h-2 mx-2 bg-gradient-to-r from-green-200 via-yellow-200 to-red-200 rounded" />
                    <span className="text-lg">€{aiSuggestion.price_range?.max || '-'}</span>
                  </div>
                  <p className="text-center text-sm text-gray-600 mt-2">
                    Ottimale: <strong>€{aiSuggestion.price_range?.optimal || aiSuggestion.suggested_price}</strong>
                  </p>
                </div>

                {/* Info */}
                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={getStrategyColor(aiSuggestion.strategy)}>
                      {aiSuggestion.strategy || 'competitiva'}
                    </Badge>
                    <Badge variant="outline">
                      Confidenza: {aiSuggestion.confidence || 'media'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">{aiSuggestion.reasoning}</p>
                </div>
              </div>

              {/* Tips */}
              {aiSuggestion.tips && aiSuggestion.tips.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 mb-1">💡 Suggerimenti:</p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    {aiSuggestion.tips.map((tip, i) => (
                      <li key={i}>• {tip}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Apply Button */}
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAiSuggestion(null)}>
                  Ignora
                </Button>
                <Button 
                  onClick={applySuggestion}
                  disabled={applying}
                  className="bg-[#C5A059] hover:bg-[#B08A3E]"
                  data-testid="apply-suggestion-btn"
                >
                  {applying ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Applicando...</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> Applica €{aiSuggestion.suggested_price}/notte</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Prezzo Medio</p>
                  <p className="text-2xl font-bold">€{combinedStats.avg_price || '-'}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-[#C5A059]" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Prezzo Min</p>
                  <p className="text-2xl font-bold text-green-600">€{combinedStats.min_price || '-'}</p>
                </div>
                <TrendingDown className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Prezzo Max</p>
                  <p className="text-2xl font-bold text-red-600">€{combinedStats.max_price || '-'}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Competitor</p>
                  <p className="text-2xl font-bold">{combinedStats.competitors_count || allCompetitors.length}</p>
                </div>
                <Building2 className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* I Tuoi Competitor (Personalizzati) */}
        <Card className="border-2 border-blue-200">
          <CardHeader>
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  I Tuoi Competitor
                </CardTitle>
                <CardDescription>
                  Strutture che monitori manualmente • I prezzi che inserisci verranno usati per l'analisi AI
                </CardDescription>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button 
                  onClick={() => {
                    resetCompetitorForm();
                    setAddCompetitorOpen(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="add-competitor-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Aggiungi
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setScrapeDialogOpen(true)}
                  className="border-green-500 text-green-600 hover:bg-green-50"
                  data-testid="scrape-url-btn"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Scraping URL
                </Button>
                {customCompetitors.length > 0 && (
                  <>
                    <Button 
                      variant="outline"
                      onClick={() => setBatchUpdateOpen(true)}
                      className="border-amber-500 text-amber-600 hover:bg-amber-50"
                      data-testid="batch-update-btn"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Aggiorna Prezzi
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={openHistoryDialog}
                      className="border-purple-500 text-purple-600 hover:bg-purple-50"
                      data-testid="price-history-btn"
                    >
                      <History className="w-4 h-4 mr-2" />
                      Storico
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {customCompetitors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Nessun competitor aggiunto.</p>
                <p className="text-sm">Aggiungi le strutture che vuoi monitorare per ottenere suggerimenti più accurati.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {customCompetitors.map((comp, idx) => (
                  <div 
                    key={comp.id || idx}
                    className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                        {idx + 1}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{comp.name}</p>
                          {comp.link && (
                            <a href={comp.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {comp.type} • {comp.source}
                          {comp.notes && ` • ${comp.notes}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {comp.rating && (
                        <Badge variant="outline" className="bg-yellow-50">
                          ⭐ {comp.rating}
                        </Badge>
                      )}
                      <div className="text-right">
                        <span className="text-lg font-bold text-blue-700">€{comp.price_per_night}</span>
                        <p className="text-xs text-gray-500">/notte</p>
                      </div>
                      <div className="flex gap-1">
                        {comp.link && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => scrapeCompetitorPrice(comp.id)}
                            disabled={scrapingId === comp.id}
                            className="h-8 w-8 p-0 hover:bg-green-100"
                            title="Aggiorna prezzo da Booking"
                          >
                            {scrapingId === comp.id ? (
                              <RefreshCw className="w-4 h-4 text-green-600 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4 text-green-600" />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => editCompetitor(comp)}
                          className="h-8 w-8 p-0 hover:bg-blue-200"
                        >
                          <Pencil className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteCompetitor(comp.id)}
                          className="h-8 w-8 p-0 hover:bg-red-100"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Competitor Suggeriti (Mock) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[#C5A059]" />
              Competitor Suggeriti
            </CardTitle>
            <CardDescription>
              Strutture simili a Paestum (dati stimati) • Periodo: {checkin} → {checkout}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {competitors.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                Nessun competitor trovato. Prova a cambiare le date.
              </p>
            ) : (
              <div className="space-y-2">
                {competitors.map((comp, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C5A059] to-[#D4AF37] flex items-center justify-center text-white font-bold">
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-medium">{comp.name}</p>
                        <p className="text-xs text-gray-500">
                          {comp.type} • {comp.source}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {comp.rating && (
                        <Badge variant="outline" className="bg-yellow-50">
                          ⭐ {comp.rating}
                        </Badge>
                      )}
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          {getPriceComparisonIcon(comp.price_per_night)}
                          <span className="text-lg font-bold">€{comp.price_per_night}</span>
                        </div>
                        <p className="text-xs text-gray-500">/notte</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Settings Dialog */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Impostazioni Dynamic Pricing</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Auto Pricing Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Pricing Automatico</Label>
                  <p className="text-xs text-gray-500">Applica automaticamente i suggerimenti AI</p>
                </div>
                <Switch
                  checked={settings.auto_apply_suggestions}
                  onCheckedChange={(checked) => 
                    setSettings({ ...settings, auto_apply_suggestions: checked })
                  }
                />
              </div>

              {/* Strategy */}
              <div>
                <Label className="font-medium">Strategia di Prezzo</Label>
                <Select
                  value={settings.price_strategy}
                  onValueChange={(value) => 
                    setSettings({ ...settings, price_strategy: value })
                  }
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aggressiva">
                      🔥 Aggressiva - Sotto la media di mercato
                    </SelectItem>
                    <SelectItem value="competitiva">
                      ⚖️ Competitiva - Allineata al mercato
                    </SelectItem>
                    <SelectItem value="premium">
                      💎 Premium - Sopra la media di mercato
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Price Limits */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Prezzo Minimo (€)</Label>
                  <Input
                    type="number"
                    value={settings.min_price}
                    onChange={(e) => 
                      setSettings({ ...settings, min_price: parseFloat(e.target.value) })
                    }
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Prezzo Massimo (€)</Label>
                  <Input
                    type="number"
                    value={settings.max_price}
                    onChange={(e) => 
                      setSettings({ ...settings, max_price: parseFloat(e.target.value) })
                    }
                  />
                </div>
              </div>

              {settings.auto_apply_suggestions && (
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Con il pricing automatico attivo, i suggerimenti AI verranno applicati 
                    automaticamente entro i limiti di prezzo impostati.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSettingsOpen(false)}>
                Annulla
              </Button>
              <Button onClick={saveSettings} className="bg-[#C5A059] hover:bg-[#B08A3E]">
                Salva Impostazioni
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Aggiungi/Modifica Competitor */}
        <Dialog open={addCompetitorOpen} onOpenChange={setAddCompetitorOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingCompetitor ? 'Modifica Competitor' : 'Aggiungi Competitor'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-medium">Nome Struttura *</Label>
                <Input
                  placeholder="Es: Villa Rosa Paestum"
                  value={competitorForm.name}
                  onChange={(e) => setCompetitorForm({ ...competitorForm, name: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Prezzo/Notte (€) *</Label>
                  <Input
                    type="number"
                    placeholder="Es: 95"
                    value={competitorForm.price_per_night}
                    onChange={(e) => setCompetitorForm({ ...competitorForm, price_per_night: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Rating</Label>
                  <Input
                    placeholder="Es: 9.2"
                    value={competitorForm.rating}
                    onChange={(e) => setCompetitorForm({ ...competitorForm, rating: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Link (Booking/Airbnb)</Label>
                <Input
                  placeholder="https://www.booking.com/hotel/..."
                  value={competitorForm.link}
                  onChange={(e) => setCompetitorForm({ ...competitorForm, link: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Fonte</Label>
                  <Select
                    value={competitorForm.source}
                    onValueChange={(value) => setCompetitorForm({ ...competitorForm, source: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="booking.com">Booking.com</SelectItem>
                      <SelectItem value="airbnb">Airbnb</SelectItem>
                      <SelectItem value="altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Tipo Struttura</Label>
                  <Select
                    value={competitorForm.type}
                    onValueChange={(value) => setCompetitorForm({ ...competitorForm, type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Casa vacanze">Casa vacanze</SelectItem>
                      <SelectItem value="B&B">B&B</SelectItem>
                      <SelectItem value="Appartamento">Appartamento</SelectItem>
                      <SelectItem value="Agriturismo">Agriturismo</SelectItem>
                      <SelectItem value="Hotel">Hotel</SelectItem>
                      <SelectItem value="Villa">Villa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Note</Label>
                <Input
                  placeholder="Es: Competitor principale, vicino ai templi..."
                  value={competitorForm.notes}
                  onChange={(e) => setCompetitorForm({ ...competitorForm, notes: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setAddCompetitorOpen(false);
                resetCompetitorForm();
              }}>
                Annulla
              </Button>
              <Button onClick={saveCompetitor} className="bg-blue-600 hover:bg-blue-700">
                {editingCompetitor ? 'Salva Modifiche' : 'Aggiungi Competitor'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Batch Update Prezzi */}
        <Dialog open={batchUpdateOpen} onOpenChange={setBatchUpdateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-amber-600" />
                Aggiorna Prezzi in Batch
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                Aggiorna i prezzi di tutti i tuoi {customCompetitors.length} competitor contemporaneamente.
              </p>
              
              <div>
                <Label className="text-sm font-medium">Tipo di Aggiornamento</Label>
                <Select
                  value={batchUpdateForm.adjustment_type}
                  onValueChange={(value) => setBatchUpdateForm({ ...batchUpdateForm, adjustment_type: value, adjustment_value: '' })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">📈 Variazione Percentuale</SelectItem>
                    <SelectItem value="fixed">💰 Prezzo Fisso</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium">
                  {batchUpdateForm.adjustment_type === 'percentage' 
                    ? 'Percentuale (usa - per diminuire)' 
                    : 'Nuovo Prezzo (€)'}
                </Label>
                <div className="relative mt-2">
                  <Input
                    type="number"
                    placeholder={batchUpdateForm.adjustment_type === 'percentage' ? 'Es: 10 o -5' : 'Es: 95'}
                    value={batchUpdateForm.adjustment_value}
                    onChange={(e) => setBatchUpdateForm({ ...batchUpdateForm, adjustment_value: e.target.value })}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {batchUpdateForm.adjustment_type === 'percentage' ? '%' : '€'}
                  </span>
                </div>
                {batchUpdateForm.adjustment_type === 'percentage' && (
                  <p className="text-xs text-gray-500 mt-1">
                    +10 = aumenta del 10%, -5 = diminuisce del 5%
                  </p>
                )}
              </div>

              {batchUpdateForm.adjustment_value && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-800">
                    {batchUpdateForm.adjustment_type === 'percentage' ? (
                      <>
                        ⚠️ Tutti i prezzi verranno {parseFloat(batchUpdateForm.adjustment_value) >= 0 ? 'aumentati' : 'diminuiti'} del <strong>{Math.abs(parseFloat(batchUpdateForm.adjustment_value))}%</strong>
                      </>
                    ) : (
                      <>
                        ⚠️ Tutti i competitor avranno prezzo <strong>€{batchUpdateForm.adjustment_value}</strong>/notte
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBatchUpdateOpen(false)}>
                Annulla
              </Button>
              <Button 
                onClick={executeBatchUpdate}
                disabled={batchUpdating || !batchUpdateForm.adjustment_value}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-batch-update-btn"
              >
                {batchUpdating ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Aggiornando...</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" /> Applica a Tutti</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Storico Prezzi */}
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-purple-600" />
                Storico Variazioni Prezzi
              </DialogTitle>
            </DialogHeader>
            
            {/* Filtro periodo */}
            <div className="flex items-center gap-4 py-2 border-b">
              <Label className="text-sm text-gray-600">Periodo:</Label>
              <Select
                value={historyDays.toString()}
                onValueChange={(value) => {
                  setHistoryDays(parseInt(value));
                  loadPriceHistory(parseInt(value));
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                  <SelectItem value="90">Ultimi 90 giorni</SelectItem>
                  <SelectItem value="180">Ultimi 6 mesi</SelectItem>
                  <SelectItem value="365">Ultimo anno</SelectItem>
                </SelectContent>
              </Select>
              
              {priceHistory.stats?.total_changes > 0 && (
                <Badge variant="outline" className="ml-auto">
                  {priceHistory.stats.total_changes} variazioni
                </Badge>
              )}
            </div>

            {/* Contenuto */}
            <div className="flex-1 overflow-y-auto">
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
                </div>
              ) : priceHistory.history?.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Nessuna variazione registrata</p>
                  <p className="text-sm">Le variazioni verranno tracciate quando modifichi i prezzi dei competitor</p>
                </div>
              ) : (
                <div className="space-y-4 py-4">
                  {/* Summary per competitor */}
                  {priceHistory.stats?.by_competitor && Object.keys(priceHistory.stats.by_competitor).length > 0 && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                      {Object.entries(priceHistory.stats.by_competitor).map(([id, data]) => (
                        <div key={id} className="p-3 bg-gray-50 rounded-lg">
                          <p className="font-medium text-sm truncate">{data.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-gray-500">{data.num_changes} modifiche</span>
                            <div className={`flex items-center gap-1 text-sm font-medium ${
                              data.total_change_pct > 0 ? 'text-red-600' : 
                              data.total_change_pct < 0 ? 'text-green-600' : 'text-gray-600'
                            }`}>
                              {data.total_change_pct > 0 ? <ChevronUp className="w-4 h-4" /> : 
                               data.total_change_pct < 0 ? <ChevronDown className="w-4 h-4" /> : null}
                              {data.total_change_pct > 0 ? '+' : ''}{data.total_change_pct}%
                            </div>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            €{data.first_price} → €{data.current_price}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Lista variazioni */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Cronologia Dettagliata</h4>
                    {priceHistory.history.map((h, idx) => (
                      <div 
                        key={h.id || idx}
                        className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            h.change_percentage > 0 ? 'bg-red-100' : 
                            h.change_percentage < 0 ? 'bg-green-100' : 'bg-gray-100'
                          }`}>
                            {h.change_percentage > 0 ? (
                              <TrendingUp className="w-4 h-4 text-red-600" />
                            ) : h.change_percentage < 0 ? (
                              <TrendingDown className="w-4 h-4 text-green-600" />
                            ) : (
                              <Minus className="w-4 h-4 text-gray-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{h.competitor_name}</p>
                            <p className="text-xs text-gray-500">
                              {formatDate(h.recorded_at)} • {getChangeTypeLabel(h.change_type)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">€{h.old_price}</span>
                            <ArrowRight className="w-3 h-3 text-gray-400" />
                            <span className="text-sm font-medium">€{h.new_price}</span>
                          </div>
                          <p className={`text-xs font-medium ${
                            h.change_percentage > 0 ? 'text-red-600' : 
                            h.change_percentage < 0 ? 'text-green-600' : 'text-gray-500'
                          }`}>
                            {h.change_percentage > 0 ? '+' : ''}{h.change_percentage}%
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="border-t pt-4">
              <Button variant="outline" onClick={() => setHistoryOpen(false)}>
                Chiudi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Scraping URL */}
        <Dialog open={scrapeDialogOpen} onOpenChange={(open) => {
          setScrapeDialogOpen(open);
          if (!open) {
            setScrapeResult(null);
            setScrapeForm({ url: '', checkin: '', checkout: '', name: '' });
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-green-600" />
                Scraping Prezzo da URL
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                Inserisci l'URL di Booking.com o Airbnb per estrarre automaticamente il prezzo.
              </p>
              
              <div>
                <Label className="text-sm font-medium">URL della struttura *</Label>
                <Input
                  placeholder="https://www.booking.com/hotel/it/..."
                  value={scrapeForm.url}
                  onChange={(e) => setScrapeForm({ ...scrapeForm, url: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium">Check-in</Label>
                  <Input
                    type="date"
                    value={scrapeForm.checkin}
                    onChange={(e) => setScrapeForm({ ...scrapeForm, checkin: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Check-out</Label>
                  <Input
                    type="date"
                    value={scrapeForm.checkout}
                    onChange={(e) => setScrapeForm({ ...scrapeForm, checkout: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Nome (opzionale)</Label>
                <Input
                  placeholder="Es: Casa Rubini"
                  value={scrapeForm.name}
                  onChange={(e) => setScrapeForm({ ...scrapeForm, name: e.target.value })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Se vuoto, verrà estratto automaticamente</p>
              </div>

              {/* Risultato Scraping */}
              {scrapeResult && (
                <div className={`p-4 rounded-lg ${scrapeResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {scrapeResult.success ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-700">Prezzo trovato!</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Nome:</span>
                          <p className="font-medium">{scrapeResult.name || scrapeForm.name || '-'}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Prezzo:</span>
                          <p className="font-bold text-xl text-green-700">€{scrapeResult.price}</p>
                        </div>
                        {scrapeResult.rating && (
                          <div>
                            <span className="text-gray-600">Rating:</span>
                            <p className="font-medium">⭐ {scrapeResult.rating}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-700">
                      <TrendingDown className="w-5 h-5" />
                      <span>{scrapeResult.error || 'Scraping fallito'}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setScrapeDialogOpen(false)}>
                Annulla
              </Button>
              {scrapeResult?.success ? (
                <Button 
                  onClick={addCompetitorFromScrape}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Aggiungi Competitor
                </Button>
              ) : (
                <Button 
                  onClick={executeScrape}
                  disabled={scrapeLoading || !scrapeForm.url}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {scrapeLoading ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analizzando...</>
                  ) : (
                    <><Search className="w-4 h-4 mr-2" /> Estrai Prezzo</>
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Search Results Dialog */}
        <Dialog open={aiSearchDialogOpen} onOpenChange={setAiSearchDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Analisi Mercato AI - {aiSearchResult?.period}
              </DialogTitle>
              <DialogDescription>
                Analisi dei prezzi competitor basata su AI per la zona Paestum
              </DialogDescription>
            </DialogHeader>

            {aiSearchResult?.analysis && (
              <div className="space-y-6">
                {/* Market Analysis */}
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-semibold text-purple-800 mb-3">📊 Analisi Mercato</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-xs text-gray-500">Stagione</p>
                      <p className="font-bold text-purple-700 capitalize">
                        {aiSearchResult.analysis.market_analysis?.season || 'N/A'}
                      </p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-xs text-gray-500">Domanda</p>
                      <p className="font-bold text-purple-700 capitalize">
                        {aiSearchResult.analysis.market_analysis?.demand_level || 'N/A'}
                      </p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-xs text-gray-500">Weekend</p>
                      <p className="font-bold text-purple-700">
                        {aiSearchResult.analysis.market_analysis?.weekend_factor ? '✅ Sì' : '❌ No'}
                      </p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-xs text-gray-500">Eventi</p>
                      <p className="font-bold text-purple-700">{aiSearchResult.events_count || 0}</p>
                    </div>
                  </div>
                  {aiSearchResult.analysis.market_analysis?.events_impact && (
                    <p className="mt-2 text-sm text-purple-600">
                      💡 {aiSearchResult.analysis.market_analysis.events_impact}
                    </p>
                  )}
                </div>

                {/* Price Ranges */}
                {aiSearchResult.analysis.price_ranges && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-3">💰 Range Prezzi Zona</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-green-100 rounded-lg">
                        <p className="text-xs text-green-600">Budget</p>
                        <p className="text-lg font-bold text-green-700">
                          €{aiSearchResult.analysis.price_ranges.budget?.min}-{aiSearchResult.analysis.price_ranges.budget?.max}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-yellow-100 rounded-lg">
                        <p className="text-xs text-yellow-600">Medio</p>
                        <p className="text-lg font-bold text-yellow-700">
                          €{aiSearchResult.analysis.price_ranges.mid_range?.min}-{aiSearchResult.analysis.price_ranges.mid_range?.max}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-purple-100 rounded-lg">
                        <p className="text-xs text-purple-600">Premium</p>
                        <p className="text-lg font-bold text-purple-700">
                          €{aiSearchResult.analysis.price_ranges.premium?.min}-{aiSearchResult.analysis.price_ranges.premium?.max}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recommended Price */}
                {aiSearchResult.analysis.recommended_price && (
                  <div className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg border-2 border-amber-300">
                    <h4 className="font-semibold text-amber-800 mb-2">🎯 Prezzo Consigliato per Te</h4>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-4xl font-bold text-amber-700">
                          €{aiSearchResult.analysis.recommended_price.suggested}
                        </p>
                        <p className="text-xs text-amber-600">
                          Range: €{aiSearchResult.analysis.recommended_price.min} - €{aiSearchResult.analysis.recommended_price.max}
                        </p>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-amber-700 mb-2">
                          {aiSearchResult.analysis.recommended_price.reasoning}
                        </p>
                        <Button 
                          onClick={applyAISuggestedPrice}
                          className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold"
                        >
                          ✅ Applica €{aiSearchResult.analysis.recommended_price.suggested}/notte per {aiSearchResult.period}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Competitors Found */}
                {aiSearchResult.analysis.competitors && aiSearchResult.analysis.competitors.length > 0 && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-3">
                      🏠 Competitor Trovati ({aiSearchResult.analysis.competitors.length})
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {aiSearchResult.analysis.competitors.map((comp, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-white rounded border">
                          <div>
                            <p className="font-medium">{comp.name}</p>
                            <p className="text-xs text-gray-500">{comp.type} - {comp.notes}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">€{comp.estimated_price}/notte</p>
                            {comp.rating && <p className="text-xs text-yellow-600">⭐ {comp.rating}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      ✅ I competitor sono stati aggiunti automaticamente alla tua lista
                    </p>
                  </div>
                )}

                {/* Events */}
                {aiSearchResult.events && aiSearchResult.events.length > 0 && (
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <h4 className="font-semibold text-orange-800 mb-2">📅 Eventi nel Periodo</h4>
                    <ul className="text-sm text-orange-700 space-y-1">
                      {aiSearchResult.events.map((ev, i) => (
                        <li key={i}>• {ev.titolo} ({ev.data}) - {ev.luogo}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setAiSearchDialogOpen(false)}>
                Chiudi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
